import React, { useState, useEffect, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import image from '../assets/fondos/FacturaGuia.png';
import VisorImagenModal from './VisorImagenModal'; // Importar el nuevo componente

const DescargaFactura = () => {
  const [codigoUsuario, setCodigoUsuario] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [indexando, setIndexando] = useState(false);
  const [estadoInfo, setEstadoInfo] = useState(null);
  const [modalAbierto, setModalAbierto] = useState(false); // Estado para el modal
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef();
  const [mensaje, setMensaje] = useState("");



  // Funciones para manejar el modal
  const abrirModal = () => setModalAbierto(true);
  const cerrarModal = () => setModalAbierto(false);

  // Verificar estado de indexación
  useEffect(() => {
    verificarEstado();
    const interval = setInterval(verificarEstado, 10000); // Cada 10 segundos
    return () => clearInterval(interval);
  }, []);

  const verificarEstado = async () => {
  try {
    const response = await fetch('/api/estado-indexacion');
    const data = await response.json();

    if (indexando && !data.en_proceso) {
      setMensaje("La indexación ha finalizado correctamente.");
    }

    setIndexando(data.en_proceso);
    setEstadoInfo(data);
  } catch (err) {
    console.error('Error al verificar estado:', err);
  }
};


  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    setError(''); // Limpiar errores al resolver el captcha
  };

  const handleDescarga = async (e) => {
    e.preventDefault();
    
    if (!codigoUsuario) {
      setError('Por favor, ingrese su código de usuario.');
      return;
    }

    if (!recaptchaToken) {
      setError('Por favor, complete el reCAPTCHA.');
      return;
    }

    if (indexando) {
      setError('El sistema está en mantenimiento. Por favor, intente en unos minutos.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await fetch('/api/descargar-factura', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/pdf',
        },
        body: JSON.stringify({ 
          codigo_usuario: codigoUsuario,
          'g-recaptcha-response': recaptchaToken,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        if (response.status === 422) { // Error de validación de reCAPTCHA
          throw new Error('No se pudo verificar el reCAPTCHA. Por favor, intente de nuevo.');
        }
        if (response.status === 503) {
          throw new Error(errorData.error || 'Sistema en mantenimiento. Intente en unos minutos.');
        }
        if (response.status === 404) {
          throw new Error('No se encontró una factura con el código de usuario proporcionado.');
        }
        throw new Error('Error en la respuesta del servidor.');
      }

      const blob = await response.blob();
      const fileURL = window.URL.createObjectURL(blob);
      const fileLink = document.createElement('a');
      fileLink.href = fileURL;

      const contentDisposition = response.headers.get('content-disposition');
      let fileName = 'factura.pdf';
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch && fileNameMatch.length === 2) {
          fileName = fileNameMatch[1];
        }
      }

      fileLink.setAttribute('download', fileName);
      document.body.appendChild(fileLink);
      fileLink.click();

      fileLink.remove();
      window.URL.revokeObjectURL(fileURL);

    } catch (err) {
      setError(err.message || 'Ocurrió un error al descargar la factura. Por favor, intente más tarde.');
      console.error(err);
    } finally {
      setLoading(false);
      // Resetear reCAPTCHA
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setRecaptchaToken(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 font-sans">
      {/* Alerta de indexación en proceso */}
      {indexando && (
        <div className="max-w-4xl mx-auto mb-4 p-4 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 rounded">
          <div className="flex items-center">
            <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-semibold">Sistema en actualización</p>
              <p className="text-sm">Disculpa, en este momento estamos actualizando los datos. Por favor intenta en unos minutos.</p>
            </div>
          </div>
        </div>
      )}

      {/* Formulario de descarga */}
      <div className="max-w-md mx-auto p-8 bg-white border border-gray-300 rounded-lg shadow-md">
        <h2 className="text-center text-gray-800 text-xl font-semibold mb-6">Descargar Factura</h2>
        <div className="mb-4">
          <label htmlFor="codigoUsuario" className="block mb-2 text-gray-600">
            Código de Usuario:
          </label>
          <input
            type="text"
            id="codigoUsuario"
            value={codigoUsuario}
            onChange={(e) => setCodigoUsuario(e.target.value)}
            placeholder="Ingrese su código"
            disabled={indexando || loading}
            className={`w-full p-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 ${
              (indexando || loading) ? 'bg-gray-100 cursor-not-allowed' : ''
            }`}
          />
        </div>

        <div className="mb-4 flex justify-center">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey="6LcYRyEsAAAAAB253dmKGtaV_3_rhIBkZwIHIKTa"
            onChange={handleRecaptchaChange}
          />
        </div>

        <button
          onClick={handleDescarga}
          disabled={loading || indexando}
          className={`w-full p-3 rounded text-white text-base font-medium transition-colors ${
            (loading || indexando)
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
          }`}
        >
          {loading ? 'Descargando...' : indexando ? 'Sistema en actualización...' : 'Descargar'}
        </button>
        {error && (
          <p className="text-red-500 text-center mt-4">{error}</p>
        )}

        {/* Info de última actualización */}
        {!indexando && estadoInfo?.ultima_actualizacion && (
          <p className="text-xs text-gray-500 text-center mt-3">
            Última actualización: {new Date(estadoInfo.ultima_actualizacion).toLocaleString('es-ES')}
          </p>
        )}
      </div>

      {/* Sección de instrucciones e imágenes */}
      <div className=" flex justify-between gap-4 mt-6 max-w-4xl mx-auto">
        {/* Instrucciones a la izquierda */}
        <div className="p-4 bg-primary border border-gray-300 text-white rounded-lg w-1/2">
          <p className="font-semibold mb-3">Instrucciones para la descarga de la factura:</p>
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>Ingrese su código de usuario en el campo proporcionado.</li>
            <li>Complete el reCAPTCHA.</li>
            <li>Haga clic en el botón "Descargar".</li>
            <li>Espere a que se complete la descarga.</li>
            <li>Si encuentra algún error, asegúrese de que el código ingresado sea correcto o intente nuevamente más tarde.</li>
          </ol>
        </div>

        {/* Imágenes de referencia a la derecha */}
        <div className="p-4 bg-white border border-gray-300 rounded-lg w-1/2">
          <p className="font-semibold mb-3 text-gray-700">Imágenes de referencia:</p>
          <div className="space-y-3">
            <div className="h-50 w-full bg-gray-200 rounded flex items-center justify-center text-gray-500 cursor-pointer" onClick={abrirModal}>
              <img 
              src = {image}
              alt="Referencia de factura"
              className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Componente Modal */}
      <VisorImagenModal 
        isOpen={modalAbierto} 
        onClose={cerrarModal} 
        imageUrl={image} 
      />
    </div>
  );
};

export default DescargaFactura;